import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { youtubeChannels, type YoutubeChannel } from '@/mocks/youtube';
import { formatCount } from '@/lib/media';

const CATEGORY_COLORS: Record<YoutubeChannel['category'], string> = {
  Comédie: 'bg-accent-500',
  Actu: 'bg-secondary-500',
  Vlog: 'bg-primary-500',
  Musique: 'bg-accent-400',
  Interview: 'bg-secondary-400',
};

function YoutubeFeed() {
  const [filter, setFilter] = useState<'Tous' | YoutubeChannel['category']>('Tous');
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef<number | null>(null);

  const categories: Array<'Tous' | YoutubeChannel['category']> = [
    'Tous',
    'Comédie',
    'Actu',
    'Vlog',
    'Musique',
    'Interview',
  ];

  const filtered = useMemo(
    () => (filter === 'Tous' ? youtubeChannels : youtubeChannels.filter((c) => c.category === filter)),
    [filter],
  );

  const goTo = useCallback(
    (i: number) => {
      if (filtered.length === 0) return;
      setIndex((i + filtered.length) % filtered.length);
    },
    [filtered.length],
  );

  // Réinitialise le carrousel quand on change de filtre
  useEffect(() => {
    setIndex(0);
  }, [filter]);

  // Défilement automatique
  useEffect(() => {
    if (filtered.length <= 1 || paused) return;
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % filtered.length);
    }, 3000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [filtered.length, paused]);

  if (filtered.length === 0) return null;

  const safeIndex = index >= filtered.length ? 0 : index;
  const current = filtered[safeIndex] ?? filtered[0];

  return (
    <div>
      {/* Filtres */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar mb-6">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              filter === c
                ? 'bg-accent-500 text-foreground-950'
                : 'bg-background-50 text-foreground-400 hover:text-foreground-50 border border-background-300/60'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Grand carrousel auto */}
      <div
        className="relative w-full h-[380px] md:h-[500px] rounded-xl overflow-hidden border border-background-200/70 bg-background-950"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Slide courante */}
        <a
          key={current.id}
          href={current.link_url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="absolute inset-0 block carousel-fade-in"
        >
          <img
            src={current.thumbnail_url}
            alt={current.channel}
            title={`${current.channel} — ${current.title}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/15" />

          {/* Contenu */}
          <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-10 max-w-3xl">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-semibold text-foreground-950 ${CATEGORY_COLORS[current.category]}`}
              >
                {current.category}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-foreground-50/90">
                <i className="ri-user-follow-line" />
                {formatCount(current.subscribers)} abonnés
              </span>
            </div>
            <h3 className="font-heading font-bold text-xl md:text-3xl text-foreground-50 leading-tight mb-2">
              {current.title}
            </h3>
            <p className="text-sm md:text-base text-foreground-50/80 leading-relaxed mb-4 line-clamp-2 max-w-2xl">
              {current.description}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground-50">
                <i className="ri-youtube-line text-accent-400 text-lg" />
                {current.channel}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-foreground-50/80">
                <i className="ri-video-line" />
                {current.video_count} vidéos
              </span>
            </div>
          </div>

          {/* Bouton lecture */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm text-foreground-50 border border-foreground-50/30 transition-transform duration-300 hover:scale-110 z-10">
            <i className="ri-play-fill text-3xl md:text-4xl" />
          </div>
        </a>

        {/* Flèches */}
        <button
          onClick={() => goTo(index - 1)}
          className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm text-foreground-50 hover:bg-background-950/70 transition-colors z-20"
          aria-label="Vidéo précédente"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          onClick={() => goTo(index + 1)}
          className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm text-foreground-50 hover:bg-background-950/70 transition-colors z-20"
          aria-label="Vidéo suivante"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>

        {/* Points */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20 flex-wrap max-w-full px-4">
          {filtered.map((v, i) => (
            <button
              key={v.id}
              onClick={() => goTo(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === safeIndex ? 'w-6 bg-primary-500' : 'w-1.5 bg-foreground-50/60'
              }`}
              aria-label={`Aller à la vidéo ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Compteur de lecture automatique */}
      <div className="flex items-center justify-between mt-4 text-xs text-foreground-400">
        <span className="inline-flex items-center gap-1.5">
          <i className="ri-refresh-line" />
          Défilement automatique {paused ? 'en pause' : 'actif'}
        </span>
        <span>
          {safeIndex + 1} / {filtered.length}
        </span>
      </div>

      {/* Grille de vignettes cliquables */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-base text-foreground-50 flex items-center gap-2">
            <i className="ri-layout-grid-line text-accent-400" />
            Toutes les vidéos
          </h3>
          <span className="text-xs text-foreground-400">{filtered.length} chaînes</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {filtered.map((channel, i) => (
            <button
              key={channel.id}
              onClick={() => goTo(i)}
              className={`group text-left rounded-lg overflow-hidden border transition-all duration-300 ${
                i === safeIndex
                  ? 'border-primary-500 ring-2 ring-primary-500/30'
                  : 'border-background-200/70 hover:border-background-300/70'
              }`}
              aria-label={`Afficher la vidéo ${channel.title}`}
            >
              <div className="relative aspect-video overflow-hidden bg-background-950">
                <img
                  src={channel.thumbnail_url}
                  alt={channel.channel}
                  title={`${channel.channel} — ${channel.title}`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <span
                  className={`absolute top-2 left-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold text-foreground-950 ${CATEGORY_COLORS[channel.category]}`}
                >
                  {channel.category}
                </span>
                <div className="absolute bottom-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-background-950/50 backdrop-blur-sm text-foreground-50 group-hover:bg-primary-500 group-hover:text-foreground-950 transition-colors">
                  <i className="ri-play-fill text-base" />
                </div>
                {i === safeIndex && (
                  <div className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950">
                    <i className="ri-check-line text-xs" />
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <p className="text-xs font-semibold text-foreground-50 leading-snug line-clamp-2">
                  {channel.title}
                </p>
                <p className="text-[11px] text-foreground-400 mt-1 line-clamp-1 flex items-center gap-1">
                  <i className="ri-youtube-line text-accent-400" />
                  {channel.channel}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default YoutubeFeed;